"use client"
import { useEffect, useRef, useState } from 'react'
import { socialApi, useSocial } from './client'
import { MAX_VIEW_MS, MIN_VIEW_MS, REPEAT_VIEW_AWAY_MS } from '@/lib/social/view-constants'
import type { SocialPost } from '@/lib/social/types'

type PendingView = { id: string; dwellMs: number; receipt: string }
const pending = new Map<string, Map<string, PendingView>>()
const remembered = new Map<string, Set<string>>()
const failures = new Map<string, number>()
const completedReceipts = new Set<string>()
let flushTimer: ReturnType<typeof setTimeout> | undefined
let flushing = false
let initialized = false
const keyFor = (actor: string) => `creator:seen-posts:${actor}:v1`

type CountSubscription = { actorId: string; postId: string; element: HTMLElement }
const subscriptions = new Set<CountSubscription>()
let countsTimer: ReturnType<typeof setInterval> | undefined
let firstRefresh: ReturnType<typeof setTimeout> | undefined
let refreshing = false
let lastRefresh = 0

function readingArea(element: HTMLElement) {
  return element.querySelector<HTMLElement>('[data-post-content]') ?? element
}
function inView(element: HTMLElement, qualified = false) {
  const rect = readingArea(element).getBoundingClientRect()
  const top = Math.min(96, innerHeight * 0.15), bottom = Math.min(72, innerHeight * 0.1)
  const height = Math.max(0, Math.min(rect.bottom, innerHeight - bottom) - Math.max(rect.top, top))
  const width = Math.max(0, Math.min(rect.right, innerWidth) - Math.max(rect.left, 0))
  const needed = Math.min(rect.height * 0.5, Math.max(1, (innerHeight - top - bottom) * 0.5))
  return rect.width > 0 && rect.height > 0 && width >= Math.min(rect.width, innerWidth) * 0.5 && (qualified ? height >= needed : height > 0)
}
async function refreshVisibleCounts() {
  if (refreshing || document.visibilityState !== 'visible' || Date.now() - lastRefresh < 5000) return
  const visible = [...subscriptions].filter(item => item.element.isConnected && inView(item.element))
  const actorId = visible[0]?.actorId
  const ids = [...new Set(visible.filter(item => item.actorId === actorId).map(item => item.postId))].slice(0, 30)
  if (!ids.length) return
  refreshing = true; lastRefresh = Date.now()
  try {
    const result = await socialApi<{ counts: Record<string, number> }>(`/views?ids=${encodeURIComponent(ids.join(','))}`)
    window.dispatchEvent(new CustomEvent('social:view-counts', { detail: { actorId, counts: result.counts } }))
  } catch { /* Keep the last confirmed counts and retry on the next visible tick. */ }
  finally { refreshing = false }
}
function onReturn() { void refreshVisibleCounts() }
function subscribeCounts(subscription: CountSubscription) {
  subscriptions.add(subscription)
  if (!countsTimer) {
    countsTimer = setInterval(onReturn, 15000)
    firstRefresh = setTimeout(onReturn, 750)
    window.addEventListener('focus', onReturn)
    document.addEventListener('visibilitychange', onReturn)
  }
  return () => {
    subscriptions.delete(subscription)
    if (!subscriptions.size) {
      clearInterval(countsTimer); clearTimeout(firstRefresh); countsTimer = undefined; lastRefresh = 0
      window.removeEventListener('focus', onReturn)
      document.removeEventListener('visibilitychange', onReturn)
    }
  }
}

export function viewedInThisTab(actor: string): Set<string> {
  if (!remembered.has(actor)) {
    let ids: string[] = []
    try { const parsed = JSON.parse(sessionStorage.getItem(keyFor(actor)) || '[]'); if (Array.isArray(parsed)) ids = parsed.filter(id => typeof id === 'string').slice(-3000) } catch { /* storage may be unavailable */ }
    remembered.set(actor, new Set(ids))
  }
  return remembered.get(actor)!
}

async function flush(keepalive = false) {
  if (flushing) return
  flushing = true
  try {
    for (const [actorId, queued] of pending) {
      if (!queued.size) continue
      const batch: Array<[string, PendingView]> = [], ids = new Set<string>()
      for (const entry of queued) {
        if (ids.has(entry[1].id)) continue
        batch.push(entry); ids.add(entry[1].id)
        if (batch.length === 20) break
      }
      for (const [key] of batch) queued.delete(key)
      try {
        const result = await socialApi<{ recorded: string[]; counts: Record<string, number> }>('/views', { method: 'POST', keepalive,
          body: { actorId, posts: batch.map(([, data]) => data) } })
        failures.delete(actorId)
        for (const [, data] of batch) if (result.recorded.includes(data.id)) completedReceipts.add(data.receipt)
        while (completedReceipts.size > 500) completedReceipts.delete(completedReceipts.values().next().value!)
        const seen = viewedInThisTab(actorId)
        for (const id of result.recorded) seen.add(id)
        try { sessionStorage.setItem(keyFor(actorId), JSON.stringify([...seen].slice(-3000))) } catch { /* DB remains authoritative */ }
        window.dispatchEvent(new CustomEvent('social:view-counts', { detail: { actorId, counts: result.counts } }))
      } catch (error) {
        const count = (failures.get(actorId) ?? 0) + 1
        failures.set(actorId, count)
        if (count < 3 && !(error instanceof Error && /invalid|expired|log in|too quickly/i.test(error.message))) {
          for (const [key, data] of batch) if ((queued.get(key)?.dwellMs ?? 0) < data.dwellMs) queued.set(key, data)
        }
      }
    }
  } finally { flushing = false }
  if (!keepalive && [...pending.values()].some(queue => queue.size)) scheduleFlush(5000)
}
function scheduleFlush(delay = 500) {
  if (flushTimer) return
  flushTimer = setTimeout(() => { flushTimer = undefined; void flush() }, delay)
}
function queueView(actorId: string, postId: string, receipt: string, dwellMs: number) {
  if (!pending.has(actorId)) pending.set(actorId, new Map())
  const queue = pending.get(actorId)!
  if ((queue.get(receipt)?.dwellMs ?? 0) < dwellMs) queue.set(receipt, { id: postId, receipt, dwellMs })
  scheduleFlush()
  if (!initialized) {
    initialized = true
    window.addEventListener('pagehide', () => { void flush(true) })
  }
}

export function usePostImpression(postId: string, receipt?: string, initialViews = 0, anonymousView = false) {
  const { profile } = useSocial()
  const viewerId = profile?.id
  const ref = useRef<HTMLElement>(null)
  const [count, setCount] = useState<{ postId: string; value: number }>()
  // A confirmed local update must never mask a fresher parent/API snapshot.
  const views = Math.max(initialViews, count?.postId === postId ? count.value : 0)
  const receiptRef = useRef(receipt)
  useEffect(() => { receiptRef.current = receipt }, [receipt])
  const hasReceipt = Boolean(receipt)
  useEffect(() => {
    const element = ref.current, actorId = viewerId ?? 'guest'
    if (!element) return
    const onCounts = (event: Event) => {
      const data = (event as CustomEvent<{ actorId: string; counts: Record<string, number> }>).detail
      const value = data?.counts?.[postId]
      if (data?.actorId === actorId && Number.isSafeInteger(value) && value >= 0) {
        setCount(current => ({ postId, value: Math.max(current?.postId === postId ? current.value : 0, value) }))
      }
    }
    window.addEventListener('social:view-counts', onCounts)
    const unsubscribe = subscribeCounts({ actorId, postId, element })
    return () => { unsubscribe(); window.removeEventListener('social:view-counts', onCounts) }
  }, [postId, viewerId])
  useEffect(() => {
    const element = ref.current, actorId = anonymousView ? 'guest' : viewerId
    let token = receiptRef.current
    if (!element || !actorId || !token) return
    let near = false, accumulated = 0, sent = 0, last = performance.now(), continuous = 0
    let awaySince = 0, renewing = false, disposed = false
    let lastPropReceipt: string | undefined = token
    let timer: ReturnType<typeof setInterval> | undefined
    async function renewExposure() {
      if (renewing || disposed) return
      renewing = true; awaySince = 0
      try {
        const result = await socialApi<{ post: SocialPost }>(`/posts/${encodeURIComponent(postId)}`)
        if (disposed || !result.post.viewReceipt || Boolean(result.post.anonymousView) !== anonymousView) return
        token = result.post.viewReceipt; accumulated = 0; sent = 0; continuous = 0; awaySince = 0; last = performance.now()
        window.dispatchEvent(new CustomEvent('social:view-counts', { detail: { actorId, counts: { [postId]: result.post.views ?? 0 } } }))
      } catch { /* Keep this exposure idempotent if current access was revoked. */ }
      finally { renewing = false }
    }
    // The first count needs two continuous seconds. Subsequent updates measure
    // active visible time, including pauses, up to five minutes per exposure.
    function sample(allowAccrual = true) {
      const now = performance.now(), delta = Math.min(1000, Math.max(0, now - last))
      last = now
      if (receiptRef.current !== lastPropReceipt) {
        lastPropReceipt = receiptRef.current
        if (!sent && !renewing && lastPropReceipt) { token = lastPropReceipt; accumulated = 0; continuous = 0; return }
      }
      const modal = document.querySelector('dialog[open], .social-context-menu')
      const active = allowAccrual && near && document.visibilityState === 'visible' && inView(element!, true) && !modal && !renewing
      if (active && sent && awaySince && now - awaySince >= REPEAT_VIEW_AWAY_MS) { void renewExposure(); return }
      if (active) awaySince = 0
      else if ((!near || document.visibilityState !== 'visible') && !awaySince) awaySince = now
      if (active) { continuous += delta; accumulated = Math.min(MAX_VIEW_MS, accumulated + delta) }
      else continuous = 0
      if (!sent && continuous < MIN_VIEW_MS) return
      const dwell = Math.floor(accumulated)
      if (dwell >= MIN_VIEW_MS && dwell > sent && (!sent || dwell - sent >= 15000 || dwell === MAX_VIEW_MS)) {
        queueView(actorId!, postId, token!, dwell); sent = dwell
      }
    }
    const observer = new IntersectionObserver(entries => {
      sample()
      near = entries[0].isIntersecting
      last = performance.now()
      if (near && !timer) timer = setInterval(sample, 500)
      if (!near) { continuous = 0; if (!awaySince) awaySince = performance.now(); clearInterval(timer); timer = undefined }
    }, { threshold: 0 })
    observer.observe(readingArea(element))
    // Next's back-navigation may restore an already-used server snapshot.
    // Obtain a fresh exposure instead of replaying that cached visit receipt.
    if (completedReceipts.has(token)) void renewExposure()
    const finish = () => {
      sample()
      if (sent && accumulated > sent && token) { queueView(actorId, postId, token, Math.floor(accumulated)); sent = Math.floor(accumulated) }
    }
    const onVisibility = () => { sample(false); if (document.visibilityState !== 'visible') { finish(); void flush(true) } }
    const onPageHide = () => { finish(); void flush(true) }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      disposed = true; finish(); clearInterval(timer); observer.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
      scheduleFlush()
    }
  }, [postId, viewerId, hasReceipt, anonymousView])
  return { ref, views }
}
